import { infiniteQueryOptions, queryOptions, type InfiniteData, type QueryClient } from '@tanstack/react-query'

import { api, data } from '../client'
import { qk, type DocumentListFilters } from '../query-keys'
import type { DocumentList, DocumentSummary } from '../types'
import { activityHeaders } from './passive'

export const DOCUMENTS_PAGE_SIZE = 50

export function documentsListQuery(filters: DocumentListFilters) {
  const queryKey = qk.documents.list(filters)
  return infiniteQueryOptions({
    queryKey,
    queryFn: async ({ pageParam, signal, client }) =>
      data(
        api.GET('/documents', {
          params: {
            query: {
              ...filters,
              limit: DOCUMENTS_PAGE_SIZE,
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
          signal,
          ...activityHeaders(client, queryKey),
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last: DocumentList) => last.nextCursor ?? null,
    staleTime: 30_000,
  })
}

export function documentQuery(id: string) {
  const queryKey = qk.documents.detail(id)
  return queryOptions({
    queryKey,
    queryFn: async ({ signal, client }) =>
      data(
        api.GET('/documents/{documentId}', {
          params: { path: { documentId: id } },
          signal,
          ...activityHeaders(client, queryKey),
        }),
      ),
    staleTime: 30_000,
  })
}

/** Snapshot for the Preparing screen; live changes arrive as preparation.* events. */
export function preparationQuery(id: string) {
  return queryOptions({
    queryKey: qk.documents.preparation(id),
    queryFn: async ({ signal }) =>
      data(api.GET('/documents/{documentId}/preparation', { params: { path: { documentId: id } }, signal })),
    staleTime: 0,
  })
}

/** Applies `fn` to the row of `id` in every cached documents list (SSE patches rows in place). */
export function patchDocumentInLists(
  client: QueryClient,
  id: string,
  fn: (row: DocumentSummary) => DocumentSummary | null,
): void {
  client.setQueriesData<InfiniteData<DocumentList>>({ queryKey: qk.documents.lists }, (old) => {
    if (!old) return old
    let changed = false
    const pages = old.pages.map((page) => {
      if (!page.items.some((row) => row.id === id)) return page
      changed = true
      const items: DocumentSummary[] = []
      for (const row of page.items) {
        if (row.id !== id) items.push(row)
        else {
          const next = fn(row)
          if (next) items.push(next)
        }
      }
      return { ...page, items }
    })
    return changed ? { ...old, pages } : old
  })
}
